import type { StateCreator } from "zustand";

import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import type { GenomeViewerStore } from "../store.ts";

export interface CanvasState {
  /** DNA base per pixel. This is the zoom level */
  basePerPixel: number;
  /** X position of the scroll in pixels. The canvas is not 100% of the tracks height and width. */
  scrollX: number;
  /** Y position of the scroll in pixels. The canvas is not 100% of the tracks height and width. */
  scrollY: number;
  /** Drawing setting */
  isDragging: boolean;
  /** Drawing setting */
  lastX: number;
  /** Drawing setting */
  lastY: number;
  /** Drawing setting */
  isVerticalScrolling: boolean;
  /** Drawing setting */
  isHorizontalScrolling: boolean;
  /** Drawing setting */
  scrollbarClickOffset: number;

  hoveringHorizontalScrollbar: boolean;
  hoveringVerticalScrollbar: boolean;
}

export interface CanvasActions {
  setZoom: (basePerPixel: number) => void;
  getOriginOffset(): number;
  getMaxWidth(): [number, number];
}

export type CanvasSlice = CanvasActions & CanvasState;

export const initialCanvasState = {
  basePerPixel: 250,
  scrollX: 0,
  scrollY: 0,
  isDragging: false,
  lastX: 0,
  lastY: 0,
  isHorizontalScrolling: false,
  isVerticalScrolling: false,
  scrollbarClickOffset: 0,
  hoveringHorizontalScrollbar: false,
  hoveringVerticalScrollbar: false,
};

export const createCanvasSlice: StateCreator<GenomeViewerStore, [], [], CanvasSlice> = (set, get) => ({
  ...initialCanvasState,
  setZoom(basePerPixel: number) {
    const { basePerPixel: currentBasePerPixel, canvas, scrollX } = get();
    if (basePerPixel === currentBasePerPixel) {
      return;
    }

    const centerCoordinateX = (scrollX + canvas!.width / 2) * currentBasePerPixel;

    const newScrollX = centerCoordinateX / basePerPixel - canvas!.width / 2;

    set({
      basePerPixel,
      scrollX: newScrollX,
    });
    refreshTracks();
  },
  getOriginOffset() {
    const { trackGroups, basePerPixel, scrollX } = get();

    const minBaseShift = Math.min(
      ...Array.from(trackGroups.values())
        .filter((trackGroup) => trackGroup.graphics.isVisible)
        .map((trackGroup) => trackGroup?.graphics.baseShift ?? 0),
    );

    return Math.min(scrollX, minBaseShift / basePerPixel);
  },
  getMaxWidth() {
    const { trackGroups, basePerPixel, scrollX, ctx } = get();

    const maxTrackGroupWidth =
      (Math.max(
        ...Array.from(trackGroups.values())
          .filter((trackGroup) => trackGroup.graphics.isVisible)
          .map((trackGroup) => (trackGroup?.graphics.length ?? 0) + (trackGroup?.graphics.baseShift ?? 0)),
      ) +
        1) /
      basePerPixel;

    return [
      maxTrackGroupWidth,
      Math.max(scrollX + (ctx?.canvas.width ?? 0), maxTrackGroupWidth) - get().getOriginOffset(),
    ];
  },
});
